import { staleTimes, useInfiniteQuery, useQueries } from '@platform/api-client';
import type { TypedClient } from '@platform/openapi';
import { useState } from 'react';
import { operationWindowOf } from './operation-period.ts';
import { listQuery, type ProductFilters } from './product-filters.ts';
import type { Product } from '../product.ts';
import { stockOfProduct, type ProductStock } from '../product-stock.ts';
import { queryKeys, usePageContext } from '../../usePageContext.ts';
import { fetchStockLevels } from '../../inventory/stock-levels.ts';
import { useProductCounts } from '../useCatalogQueries.ts';

export type StockCell = ProductStock | 'loading' | 'failed';

const variantIdsOf = (products: readonly Product[]): string[] =>
  products.flatMap((product) => product.variants.map((variant) => variant.id));

export const useProductList = (api: TypedClient, filters: ProductFilters) => {
  const { tenantId } = usePageContext();

  const [openedAt] = useState(() => new Date());
  const operationWindow = operationWindowOf(filters, openedAt);
  const list = useInfiniteQuery({
    queryKey: queryKeys.products(tenantId, { ...filters, operationWindow, view: 'list' }),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      api.get('/admin/products', { query: listQuery(filters, pageParam, operationWindow) }),
    getNextPageParam: (last) => last.nextPageToken ?? undefined,
    staleTime: staleTimes.catalog,
  });

  const counts = useProductCounts(api, { company: filters.company, operationWindow });

  const pages = list.data?.pages ?? [];

  const levels = useQueries({
    queries: pages.map((page) => {
      const variantIds = variantIdsOf(page.items);
      return {
        queryKey: queryKeys.stockLevels(tenantId, { variantIds }),
        queryFn: () => fetchStockLevels(api, variantIds),
        enabled: variantIds.length > 0,
        staleTime: staleTimes.inventory,
      };
    }),
  });

  const stockByProduct = new Map<string, StockCell>(
    pages.flatMap((page, position) => {
      const level = levels[position];
      return page.items.map((product): [string, StockCell] => {
        if (level === undefined || level.isError) return [product.id, 'failed'];
        if (level.data === undefined)
          return [product.id, product.variants.length === 0 ? stockOfProduct(product, []) : 'loading'];
        return [product.id, stockOfProduct(product, level.data.items)];
      });
    }),
  );

  return {
    query: list,
    counts: counts.data,
    products: pages.flatMap((page): readonly Product[] => page.items),
    total: pages[0]?.total ?? 0,
    stockOf: (product: Product): StockCell => stockByProduct.get(product.id) ?? 'loading',
    levels: levels.flatMap((level) => level.data?.items ?? []),
    retryStock: () => {
      for (const level of levels) if (level.isError) void level.refetch();
    },
    hasMore: list.hasNextPage,
    loadingMore: list.isFetchingNextPage,
    loadMore: () => {
      void list.fetchNextPage();
    },
  };
};
